import React, { useMemo } from "react";
import "./nutrientchart.css";

// ── CONFIG ──
const COLORS = ["#C6654A", "#D59E5D", "#F8E877", "#97BD76", "#9999F9"];

export interface NutrientRow {
    FieldGuid: string;
    FieldName: string;
    NutrientName: string;
    VeryLow: number;
    Low: number;
    Medium: number;
    High: number;
    VeryHigh: number;
    AvgValue: number;
}

interface Field {
    name: string;
    nutrients: NutrientRow[];
}

function fmt(v: number): string {
    if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (v >= 10) return parseFloat(v.toFixed(1)).toString();
    return parseFloat(v.toFixed(2)).toString();
}

function groupByField(data: NutrientRow[]): Field[] {
    const map = new Map<string, Field>();
    for (const row of data) {
        if (!map.has(row.FieldGuid)) {
            map.set(row.FieldGuid, { name: row.FieldName, nutrients: [] });
        }
        map.get(row.FieldGuid)!.nutrients.push(row);
    }
    return [...map.values()];
}

function NutrientBar({ nutrient }: { nutrient: NutrientRow }) {
    const { NutrientName, VeryLow, Low, Medium, High, AvgValue } = nutrient;

    const displayTop = Math.max(High * 1.5, AvgValue * 1.1);

    const bands = [
        { from: 0, to: VeryLow, color: COLORS[0] },
        { from: VeryLow, to: Low, color: COLORS[1] },
        { from: Low, to: Medium, color: COLORS[2] },
        { from: Medium, to: High, color: COLORS[3] },
        { from: High, to: displayTop, color: COLORS[4] },
    ];

    const toPercent = (v: number) => 11 + (1 - Math.min(v / High, 1)) * 89;

    const avgTop = toPercent(AvgValue);

    const mainBands = bands.slice(0, 4); // exclude the top "Very High" band

    return (
        <div className="barSection">
            <div className="barContainer">
                {/* Very High cap — always 11% tall */}
                <div style={{ height: "11%", background: COLORS[4] }} className="barPart">
                    <span>{fmt(High)}</span>
                </div>

                {mainBands.map((b, i) => {
                    const top = toPercent(b.to);
                    const bottom = toPercent(b.from);
                    const height = bottom - top;
                    if (height <= 0) return null;
                    return (
                        <div
                            key={i}
                            style={{
                                top: `${top}%`,
                                height: `${height}%`,
                                background: b.color,
                            }}
                            className="barPart"
                        >
                            <span>{fmt(b.from === 0 ? 0 : b.from)}</span>
                        </div>
                    );
                })}

                <div
                    className="avgBlock"
                    style={{ top: `${avgTop}%` }} // fix: added %
                >
                    <span>{fmt(AvgValue)}</span>
                </div>
            </div>

            <div className="nutrient">{NutrientName}</div>
        </div>
    );
}

function FieldSection({ field }: { field: Field }) {
    return (
        <div className="fieldSection">
            <div className="fieldName">{field.name}</div>
            <div className="barGroup">
                {field.nutrients.map((n) => (
                    <NutrientBar key={n.NutrientName} nutrient={n} />
                ))}
            </div>
        </div>
    );
}

function Legend({ legendData }: { legendData: string[] }) {
    if (!legendData) return <></>;
    return (
        <div className="legendContainer">
            <div className="legend">
                {legendData.map((label, i) => (
                    <div key={label} style={{ background: COLORS[i] }} className="legendBlock">
                        <span>{label}</span>
                    </div>
                ))}
                {/* #### Average legend item ####
                <div className="legendBlock" style={{ background: "#2f3742", color: "#fff" }}>
                    <span>Avg</span>
                </div>
                */}
            </div>
        </div>
    );
}

interface NutrientChartProps {
    data: NutrientRow[];
    legendData: string[];
}

export default function NutrientChart({ data, legendData }: NutrientChartProps) {
    const fields = useMemo(() => groupByField(data), [data]);

    return (
        <div className="analysisResultBarSection">
            {fields.map((field) => (
                <FieldSection key={field.name} field={field} />
            ))}
            {legendData === undefined ? <></> : <Legend legendData={legendData}></Legend>}
        </div>
    );
}
